import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Schema } from "../../api/client";
import { useAuth } from "../../app/auth/AuthProvider";
import { ErrorState, ForbiddenState, LoadingState } from "../../components/feedback/AsyncState";
import { ConfirmDialog } from "../../components/feedback/ConfirmDialog";

export function ProviderConnectionsPage(): React.JSX.Element {
  const { can } = useAuth();
  const [provider, setProvider] = useState("openai");
  const [environment, setEnvironment] = useState<Schema["ProviderConnectionEnvironment"]>("Production");
  const [reference, setReference] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [replacementReference, setReplacementReference] = useState("");
  const [circuitCursor, setCircuitCursor] = useState<string | null>(null);
  const [circuitToReset, setCircuitToReset] = useState<Schema["ProviderExecutionCircuitResponse"] | null>(null);
  const queryClient = useQueryClient();
  const connections = useQuery({ queryKey: ["provider-connections"], queryFn: api.listProviderConnections, enabled: can("platform.providers.manage") });
  const circuits = useQuery({ queryKey: ["provider-execution-circuits", circuitCursor], queryFn: () => api.listProviderExecutionCircuits(circuitCursor ?? undefined), enabled: can("platform.providers.manage"), refetchInterval: 5_000 });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["provider-connections"] });
  const create = useMutation({ mutationFn: api.createProviderConnection, onSuccess: refresh });
  const update = useMutation({ mutationFn: ({ id, etag, secretReference }: { id: string; etag: string; secretReference: string }) => api.updateProviderConnection(id, { secretReference }, etag), onSuccess: () => { setEditingId(null); setReplacementReference(""); refresh(); } });
  const verify = useMutation({ mutationFn: ({ id, etag }: { id: string; etag: string }) => api.verifyProviderConnection(id, etag), onSuccess: refresh });
  const revoke = useMutation({ mutationFn: ({ id, etag }: { id: string; etag: string }) => api.revokeProviderConnection(id, etag), onSuccess: refresh });
  const resetCircuit = useMutation({
    mutationFn: ({ id, etag }: { id: string; etag: string }) => api.resetProviderExecutionCircuit(id, etag),
    onSuccess: () => {
      setCircuitToReset(null);
      void queryClient.invalidateQueries({ queryKey: ["provider-execution-circuits"] });
    }
  });

  if (!can("platform.providers.manage")) return <ForbiddenState />;
  if (connections.isLoading) return <LoadingState label="Loading platform connections…" />;
  if (connections.error) return <ErrorState error={connections.error} retry={() => void connections.refetch()} />;

  return <section className="stack">
    <div className="page-heading">
      <div>
        <p className="eyebrow">PLATFORM ADMIN</p>
        <h1>Provider connections</h1>
        <p>Secrets remain outside ContentForge. Use only an env:NAME or a file:absolute-path reference under the configured secrets root.</p>
      </div>
    </div>
    <section className="panel">
      <form onSubmit={event => {
        event.preventDefault();
        void create.mutateAsync({ provider, environment, secretReference: reference }).then(() => setReference(""));
      }}>
        <label>Connection provider
          <select value={provider} onChange={event => setProvider(event.target.value)}>
            <option value="openai">OpenAI</option>
            <option value="runway">Runway</option>
          </select>
        </label>
        <label>Connection environment
          <select value={environment} onChange={event => setEnvironment(event.target.value as Schema["ProviderConnectionEnvironment"])}>
            <option value="Production">Production</option>
            <option value="Test">Test</option>
          </select>
        </label>
        <label>Secret reference
          <input required value={reference} placeholder="env:OPENAI_API_KEY" onChange={event => setReference(event.target.value)} />
        </label>
        <button className="button-primary" disabled={create.isPending}>{create.isPending ? "Saving…" : "Add connection"}</button>
      </form>
      {create.error && <p className="field-error">Connection was not saved. References must be env:NAME or file:path.</p>}
    </section>
    <div className="card-list">
      {(connections.data?.data ?? []).map(connection => <article className="campaign-card" key={connection.id}>
        <div>
          <h2>{connection.provider} · {connection.environment}</h2>
          <p>{connection.lifecycle} · {connection.preflightKind}</p>
          <small>{connection.lastResultCode ?? "Not checked"}{connection.lastCheckedAt ? ` · ${new Date(connection.lastCheckedAt).toLocaleString()}` : ""}</small>
          {editingId === connection.id && <form onSubmit={event => {
            event.preventDefault();
            void update.mutateAsync({ id: connection.id, etag: String(connection.version), secretReference: replacementReference });
          }}>
            <label>Replacement secret reference
              <input required value={replacementReference} placeholder="env:ROTATED_PROVIDER_KEY" onChange={event => setReplacementReference(event.target.value)} />
            </label>
            <div className="actions">
              <button type="submit" className="button-primary" disabled={update.isPending}>{update.isPending ? "Saving…" : "Save replacement"}</button>
              <button type="button" className="button-secondary" onClick={() => { setEditingId(null); setReplacementReference(""); }}>Cancel</button>
            </div>
            {update.error && <p className="field-error">The replacement reference was not saved. Refresh and try again if this connection changed.</p>}
          </form>}
        </div>
        <div className="actions">
          <button type="button" className="button-secondary" disabled={editingId !== null || connection.lifecycle === "Revoked"} onClick={() => setEditingId(connection.id)}>Replace reference</button>
          <button type="button" className="button-secondary" disabled={verify.isPending || connection.lifecycle === "Revoked"} onClick={() => void verify.mutateAsync({ id: connection.id, etag: String(connection.version) })}>Verify</button>
          <button type="button" className="button-danger" disabled={revoke.isPending || connection.lifecycle === "Revoked"} onClick={() => {
            if (window.confirm("Revoke this platform connection? Pending provider work will be stopped.")) {
              void revoke.mutateAsync({ id: connection.id, etag: String(connection.version) });
            }
          }}>Revoke</button>
        </div>
      </article>)}
    </div>
    <section className="panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">EXECUTION HEALTH</p>
          <h2>Provider admission circuits</h2>
          <p className="muted">Shared limits and circuit state update every five seconds. Reset only after correcting the provider configuration or outage.</p>
        </div>
      </div>
      {circuits.isLoading ? <LoadingState label="Loading provider execution health…" /> : circuits.error ? <ErrorState error={circuits.error} retry={() => void circuits.refetch()} /> : (circuits.data?.data?.length ?? 0) === 0 ? <p className="muted">No provider execution circuits have been created.</p> : <>
        <div className="table-wrap"><table><thead><tr><th>Provider</th><th>Capability</th><th>State</th><th>Admission</th><th>Window</th><th>Open until</th><th>Last code</th><th /></tr></thead><tbody>
          {(circuits.data!.data ?? []).map(circuit => <tr key={circuit.id}>
            <td>{circuit.provider} · {circuit.environment}</td>
            <td>{circuit.capability}</td>
            <td><span className="status">{circuit.state}</span></td>
            <td>{circuit.inFlight} / {circuit.maxConcurrency}</td>
            <td>{circuit.requestsInWindow} / {circuit.requestsPerMinute} RPM</td>
            <td>{circuit.openUntil ? new Date(circuit.openUntil).toLocaleString() : "—"}</td>
            <td><code>{circuit.lastErrorCode ?? "—"}</code></td>
            <td><button type="button" className="button-danger" disabled={resetCircuit.isPending || circuit.state === "Closed"} onClick={() => setCircuitToReset(circuit)}>Reset circuit</button></td>
          </tr>)}
        </tbody></table></div>
        {circuits.data!.meta?.nextCursor && <button type="button" className="button-secondary" onClick={() => setCircuitCursor(circuits.data!.meta!.nextCursor!)}>Load more circuits</button>}
      </>}
      {resetCircuit.error && <p className="field-error">Circuit reset was not applied. Refresh and retry if its state changed.</p>}
    </section>
    <ConfirmDialog
      open={circuitToReset !== null}
      title="Reset provider circuit"
      message={circuitToReset ? `Allow ${circuitToReset.provider} ${circuitToReset.capability} traffic to probe again? Confirm only after the connection or provider issue is corrected.` : ""}
      confirmLabel="Reset circuit"
      onCancel={() => setCircuitToReset(null)}
      onConfirm={() => {
        if (circuitToReset) void resetCircuit.mutateAsync({ id: circuitToReset.id, etag: String(circuitToReset.version) });
      }}
    />
  </section>;
}
